package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ConditionalContentAst
import top.wcpe.allincore.dsl.compiler.ast.ForContentAst
import top.wcpe.allincore.dsl.compiler.ast.DrawAst
import top.wcpe.allincore.dsl.compiler.ast.DrawInstructionAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.MenuContentAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.TooltipContentAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * DSL_E311 防御围栏：组件展开完毕后递归扫描产物，若仍存在未替换的 PropsRef
 * 则上报诊断。
 *
 * 这避免未来 [ExpressionSubstitutor] 漏处理某个 sealed 子类型时，错误要等到
 * codegen 才以 IllegalStateException 暴露。扫描深入到 LambdaExpr / IfExpr /
 * WhenExpr / NetRequestCall / AwaitExpr 的 StmtAst 分支体，与替换逻辑完全对齐。
 */
internal class PropsRefLeakDetector(
    private val diag: DiagnosticCollector,
) {

    /** 防御围栏入口：扫描整棵元素树。 */
    fun assertNoLeakedPropsRef(element: ElementAst) {
        for (pa in element.props) {
            scanPropsRefLeak(pa.value, pa.position)
        }
        for (modifier in element.modifiers) {
            for (field in modifier.fields) {
                scanPropsRefLeak(field.value, field.position)
            }
        }
        for (child in element.children) {
            assertNoLeakedPropsRef(child)
        }
        // 风险 4 遍历审计：未展开成功的组件调用仍残留具名插槽内容，其中可能含未替换的 PropsRef。
        for (content in element.namedSlotContent.values) {
            for (child in content) assertNoLeakedPropsRef(child)
        }
        // canvas 元素：attrs 与 draw/once 指令体同样需要扫描，
        // 避免 `arc(..., props.color)` 等在 draw 块内的残留漏到 codegen。
        element.canvasData?.let { canvas ->
            scanPropsRefLeak(canvas.width, canvas.position)
            scanPropsRefLeak(canvas.height, canvas.position)
            canvas.durationMs?.let { scanPropsRefLeak(it, canvas.position) }
            for (attr in canvas.attrs) {
                scanPropsRefLeak(attr.value, attr.position)
            }
            scanDrawBlockLeak(canvas.drawBlock)
            canvas.onceBlock?.let { scanDrawBlockLeak(it) }
        }
        // 与 ExpressionSubstitutor.substituteElement 同步：tooltip / toast /
        // rightClickMenu / contextMenu / menuBlocks / conditional 内未替换的 PropsRef
        // 同样必须被防御围栏发现，否则只能等到 codegen 才以 IllegalStateException 暴露。
        element.tooltipContent?.let { scanTooltipContentLeak(it, element.position) }
        element.toastContent?.let { scanTooltipContentLeak(it, element.position) }
        element.rightClickMenuContent?.let { scanTooltipContentLeak(it, element.position) }
        element.contextMenuContent?.let { scanTooltipContentLeak(it, element.position) }
        element.menuBlocks.forEach { scanMenuContentLeak(it) }
        element.conditionalContent?.let { scanConditionalContentLeak(it, element.position) }
        element.forContent?.let { scanForContentLeak(it, element.position) }
        // 数据驱动容器的 source 表达式（`source = props.list`）：与 ExpressionSubstitutor 下钻 sourceExpr 对称，
        // 否则替换漏处理时只能等 codegen 才以 IllegalStateException 暴露，而非清晰的 DSL_E311。
        element.sourceExpr?.let { scanPropsRefLeak(it, element.position) }
    }

    /** 扫描 [TooltipContentAst] 三种形态内的 PropsRef。 */
    private fun scanTooltipContentLeak(content: TooltipContentAst, fallback: SourcePosition) {
        when (content) {
            is TooltipContentAst.Text -> scanPropsRefLeak(content.value, content.position)
            is TooltipContentAst.Inline -> assertNoLeakedPropsRef(content.root)
            is TooltipContentAst.Component -> content.props.forEach { pa ->
                scanPropsRefLeak(pa.value, pa.position)
            }
        }
    }

    /** 扫描 [MenuContentAst] 块内的 PropsRef（块上 props 与 root 元素树）。 */
    private fun scanMenuContentLeak(menu: MenuContentAst) {
        menu.props.forEach { scanPropsRefLeak(it.value, it.position) }
        assertNoLeakedPropsRef(menu.root)
    }

    /** 扫描 [ConditionalContentAst] 的 subject / 各 branch / else 分支体。 */
    private fun scanConditionalContentLeak(content: ConditionalContentAst, fallback: SourcePosition) {
        content.subject?.let { scanPropsRefLeak(it, fallback) }
        content.branches.forEach { br ->
            br.condition?.let { scanPropsRefLeak(it, br.position) }
            br.values.forEach { scanPropsRefLeak(it, br.position) }
            assertNoLeakedPropsRef(br.body)
        }
        content.elseBranch?.let { assertNoLeakedPropsRef(it) }
    }

    /** 扫描 [ForContentAst] 的 iterable 表达式与循环体。 */
    private fun scanForContentLeak(content: ForContentAst, fallback: SourcePosition) {
        scanPropsRefLeak(content.iterable, fallback)
        assertNoLeakedPropsRef(content.body)
    }

    /** 深度扫描 [DrawAst] 内所有指令上的表达式，查找未替换的 PropsRef。 */
    private fun scanDrawBlockLeak(drawAst: DrawAst) {
        for (ins in drawAst.instructions) scanDrawInstructionLeak(ins)
    }

    /** 扫描单条 draw 指令携带的所有表达式（含嵌套 for/if 体）。 */
    private fun scanDrawInstructionLeak(ins: DrawInstructionAst) {
        when (ins) {
            is DrawInstructionAst.Primitive -> ins.args.forEach { scanPropsRefLeak(it, ins.position) }
            is DrawInstructionAst.ValAssign -> scanPropsRefLeak(ins.value, ins.position)
            is DrawInstructionAst.For -> ins.body.forEach { scanDrawInstructionLeak(it) }
            is DrawInstructionAst.IfElse -> {
                scanPropsRefLeak(ins.condition, ins.position)
                ins.thenBranch.forEach { scanDrawInstructionLeak(it) }
                ins.elseBranch.forEach { scanDrawInstructionLeak(it) }
            }
        }
    }

    /** 在 [expr] 里深度查找未替换的 PropsRef 并上报 DSL_E311。 */
    private fun scanPropsRefLeak(expr: ExprAst, fallback: SourcePosition) {
        when (expr) {
            is ExprAst.PropsRef -> diag.error(
                DslError.E311_PROPS_REF_LEAKED,
                expr.position,
                "组件展开后仍存在未替换的 props.${expr.name}；" +
                    "可能原因：substitutePropsInExpr 未深入 IfExpr/LambdaExpr/WhenExpr 等分支体",
            )
            is ExprAst.BinaryOp -> {
                scanPropsRefLeak(expr.left, fallback)
                scanPropsRefLeak(expr.right, fallback)
            }
            is ExprAst.UnaryOp -> scanPropsRefLeak(expr.operand, fallback)
            is ExprAst.MemberAccess -> scanPropsRefLeak(expr.target, fallback)
            is ExprAst.SafeMemberAccess -> scanPropsRefLeak(expr.target, fallback)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> scanPropsRefLeak(expr.target, fallback)
            is ExprAst.MemberCall -> {
                scanPropsRefLeak(expr.target, fallback)
                expr.args.forEach { scanPropsRefLeak(it, fallback) }
                expr.namedArgs.forEach { scanPropsRefLeak(it.value, fallback) }
            }
            is ExprAst.SafeMemberCall -> {
                scanPropsRefLeak(expr.target, fallback)
                expr.args.forEach { scanPropsRefLeak(it, fallback) }
            }
            is ExprAst.Elvis -> {
                scanPropsRefLeak(expr.left, fallback)
                scanPropsRefLeak(expr.right, fallback)
            }
            is ExprAst.Ternary -> {
                scanPropsRefLeak(expr.cond, fallback)
                scanPropsRefLeak(expr.thenExpr, fallback)
                scanPropsRefLeak(expr.elseExpr, fallback)
            }
            is ExprAst.Interp -> expr.parts.forEach { part ->
                if (part is ExprAst.Interp.InterpPart.ExprPart) scanPropsRefLeak(part.expr, fallback)
            }
            is ExprAst.ListLiteralExpr -> expr.elements.forEach { scanPropsRefLeak(it, fallback) }
            is ExprAst.MapLiteralExpr -> expr.entries.forEach { scanPropsRefLeak(it.value, fallback) }
            is ExprAst.RangeExpr -> {
                scanPropsRefLeak(expr.start, fallback)
                scanPropsRefLeak(expr.endValue, fallback)
            }
            is ExprAst.IndexExpr -> {
                scanPropsRefLeak(expr.target, fallback)
                scanPropsRefLeak(expr.index, fallback)
            }
            is ExprAst.CallExpr -> {
                scanPropsRefLeak(expr.callee, fallback)
                expr.args.forEach { scanPropsRefLeak(it, fallback) }
            }
            is ExprAst.BuiltinCallExpr -> expr.args.forEach { scanPropsRefLeak(it, fallback) }
            // D1：与 substitutePropsInExpr / walkExpr 同步深入分支体，避免 PropsRef 漏检。
            is ExprAst.IfExpr -> {
                scanPropsRefLeak(expr.cond, fallback)
                scanStmtListLeak(expr.thenBody, fallback)
                scanStmtListLeak(expr.elseBody, fallback)
            }
            is ExprAst.LambdaExpr -> scanStmtListLeak(expr.body, fallback)
            is ExprAst.WhenExpr -> {
                scanPropsRefLeak(expr.subject, fallback)
                expr.branches.forEach { br ->
                    br.values.forEach { scanPropsRefLeak(it, fallback) }
                    scanStmtListLeak(br.body, fallback)
                }
                expr.elseBody?.let { scanStmtListLeak(it, fallback) }
            }
            is ExprAst.NetRequestCall -> {
                expr.payload.forEach { scanPropsRefLeak(it.value, fallback) }
                expr.callback?.let { scanPropsRefLeak(it, fallback) }
                expr.timeoutMs?.let { scanPropsRefLeak(it, fallback) }
            }
            is ExprAst.NetSubscribeCall -> scanPropsRefLeak(expr.handler, fallback)
            is ExprAst.AwaitExpr -> {
                expr.netRequestCall?.let { scanPropsRefLeak(it, fallback) }
                expr.suspendCall?.let { scanPropsRefLeak(it, fallback) }
                expr.tailExpr?.let { scanPropsRefLeak(it, fallback) }
            }
            // M6-e：FunRefExpr 携带 boundArgs / boundNamedArgs（`::name(props.x)` 部分应用）时
            // 必须深入扫描；纯引用形态（两者都为空）即为叶子。
            is ExprAst.FunRefExpr -> {
                expr.boundArgs.forEach { scanPropsRefLeak(it, fallback) }
                expr.boundNamedArgs.forEach { scanPropsRefLeak(it.value, fallback) }
            }
            // 叶子节点
            is ExprAst.Literal, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.IdentRef,
            is ExprAst.LocalRef, is ExprAst.ThemeRef,
            is ExprAst.NullLit -> Unit
            // M5-c：IsExpr 仅下钻 target
            is ExprAst.IsExpr -> scanPropsRefLeak(expr.target, fallback)
        }
    }

    /** 在语句列表深处扫描 PropsRef 漏出。覆盖全部 StmtAst 子类型。 */
    private fun scanStmtListLeak(stmts: List<StmtAst>, fallback: SourcePosition) {
        for (s in stmts) scanStmtLeak(s, fallback)
    }

    private fun scanStmtLeak(s: StmtAst, fallback: SourcePosition) {
        when (s) {
            is StmtAst.ExprStmt -> scanPropsRefLeak(s.call, fallback)
            is StmtAst.AssignStmt -> scanPropsRefLeak(s.rhs, fallback)
            is StmtAst.VarDeclStmt -> scanPropsRefLeak(s.initializer, fallback)
            is StmtAst.IfStmt -> scanIfStmtLeak(s, fallback)
            is StmtAst.WhileStmt -> { scanPropsRefLeak(s.cond, fallback); scanStmtListLeak(s.body, fallback) }
            is StmtAst.ForInStmt -> { scanPropsRefLeak(s.iterable, fallback); scanStmtListLeak(s.body, fallback) }
            is StmtAst.WhenStmt -> scanPropsRefLeak(s.whenExpr, fallback)
            is StmtAst.ReturnStmt -> s.value?.let { scanPropsRefLeak(it, fallback) }
            is StmtAst.TryStmt -> scanTryStmtLeak(s, fallback)
            // batch 语句：递归扫描 body 内 PropsRef 漏出（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> scanStmtListLeak(s.body, fallback)
            is StmtAst.FunDeclStmt -> scanStmtListLeak(s.decl.body, fallback)
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    private fun scanIfStmtLeak(s: StmtAst.IfStmt, fallback: SourcePosition) {
        scanPropsRefLeak(s.cond, fallback)
        scanStmtListLeak(s.thenBody, fallback)
        scanStmtListLeak(s.elseBody, fallback)
    }

    private fun scanTryStmtLeak(s: StmtAst.TryStmt, fallback: SourcePosition) {
        scanStmtListLeak(s.tryBody, fallback)
        scanStmtListLeak(s.catchBody, fallback)
        scanStmtListLeak(s.finallyBody, fallback)
    }
}
